// utils/geoCalculations.ts

export interface GPSCoordinate {
  lat: number;
  lng: number;
}

export interface VideoDisplacement {
  x: number; // metros (horizontal, + direita / - esquerda)
  y: number; // metros (profundidade, sempre positivo)
}

export interface DisplacementResult {
  originalGPS: GPSCoordinate;
  adjustedGPS: GPSCoordinate;
  horizontalOffset: number;
  depthOffset: number;
  bearing: number;
}

/**
 * Constantes de conversão
 */
const EARTH_RADIUS_METERS = 6371000; // Raio da Terra em metros
const DEGREES_TO_RADIANS = Math.PI / 180;
const RADIANS_TO_DEGREES = 180 / Math.PI;

/**
 * Calcula a distância entre dois pontos GPS usando a fórmula Haversine
 */
export function calculateDistance(
  coord1: GPSCoordinate,
  coord2: GPSCoordinate
): number {
  const dLat = (coord2.lat - coord1.lat) * DEGREES_TO_RADIANS;
  const dLng = (coord2.lng - coord1.lng) * DEGREES_TO_RADIANS;

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(coord1.lat * DEGREES_TO_RADIANS) *
      Math.cos(coord2.lat * DEGREES_TO_RADIANS) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_METERS * c;
}

/**
 * Calcula o bearing (direção) entre dois pontos GPS
 */
export function calculateBearing(
  from: GPSCoordinate,
  to: GPSCoordinate
): number {
  const lat1 = from.lat * DEGREES_TO_RADIANS;
  const lat2 = to.lat * DEGREES_TO_RADIANS;
  const dLng = (to.lng - from.lng) * DEGREES_TO_RADIANS;

  const y = Math.sin(dLng) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);

  let bearing = Math.atan2(y, x) * RADIANS_TO_DEGREES;

  // Normalizar para 0-360
  bearing = (bearing + 360) % 360;

  return bearing;
}

/**
 * Desloca um ponto GPS em uma direção e distância específicas
 *
 * @param origin - Coordenada GPS de origem
 * @param bearing - Direção em graus (0-360, onde 0 é norte)
 * @param distanceMeters - Distância em metros
 * @returns Nova coordenada GPS
 */
export function displaceGPS(
  origin: GPSCoordinate,
  bearing: number,
  distanceMeters: number
): GPSCoordinate {
  const lat1 = origin.lat * DEGREES_TO_RADIANS;
  const lng1 = origin.lng * DEGREES_TO_RADIANS;
  const bearingRad = bearing * DEGREES_TO_RADIANS;
  const angularDistance = distanceMeters / EARTH_RADIUS_METERS;

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(angularDistance) +
      Math.cos(lat1) * Math.sin(angularDistance) * Math.cos(bearingRad)
  );

  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(bearingRad) * Math.sin(angularDistance) * Math.cos(lat1),
      Math.cos(angularDistance) - Math.sin(lat1) * Math.sin(lat2)
    );

  return {
    lat: lat2 * RADIANS_TO_DEGREES,
    lng: lng2 * RADIANS_TO_DEGREES
  };
}

/**
 * Calcula o deslocamento GPS ajustado baseado no bearing da câmera
 * e no deslocamento horizontal/profundidade do vídeo
 *
 * @param originalGPS - GPS original da imagem
 * @param cameraBearing - Bearing da câmera em graus (da foto)
 * @param videoDisplacement - Deslocamento calculado do vídeo (x, y)
 * @returns Resultado com GPS ajustado e informações do deslocamento
 */
export function calculateAdjustedGPSWithBearing(
  originalGPS: GPSCoordinate,
  cameraBearing: number,
  videoDisplacement: VideoDisplacement
): DisplacementResult {
  const { x: horizontalOffset, y: depthOffset } = videoDisplacement;

  console.log('📍 calculateAdjustedGPSWithBearing:', {
    originalGPS,
    cameraBearing,
    horizontalOffset,
    depthOffset
  });

  // Calcular bearing final considerando o deslocamento horizontal
  // Se x > 0 (direita), adiciona 90° ao bearing da câmera
  // Se x < 0 (esquerda), subtrai 90° do bearing da câmera
  let lateralBearing = cameraBearing;
  if (horizontalOffset !== 0) {
    // Deslocamento perpendicular à direção da câmera
    lateralBearing = cameraBearing + (horizontalOffset > 0 ? 90 : -90);
    lateralBearing = (lateralBearing + 360) % 360;
  }

  // Primeiro: aplicar deslocamento horizontal (perpendicular à câmera)
  let adjustedGPS = originalGPS;
  if (Math.abs(horizontalOffset) > 0.01) {
    adjustedGPS = displaceGPS(
      adjustedGPS,
      lateralBearing,
      Math.abs(horizontalOffset)
    );
    console.log('📍 Após deslocamento horizontal:', adjustedGPS);
  }

  // Segundo: aplicar deslocamento de profundidade (na direção da câmera)
  if (depthOffset > 0.01) {
    adjustedGPS = displaceGPS(adjustedGPS, cameraBearing, depthOffset);
    console.log('📍 Após deslocamento de profundidade:', adjustedGPS);
  }

  // Calcular bearing final do ponto original para o ajustado
  const finalBearing = calculateBearing(originalGPS, adjustedGPS);

  const result: DisplacementResult = {
    originalGPS,
    adjustedGPS,
    horizontalOffset,
    depthOffset,
    bearing: finalBearing
  };

  console.log('📍 Resultado final:', result);

  return result;
}

/**
 * Calcula o deslocamento em pixels do vídeo para metros reais
 * usando estimativas baseadas no campo de visão da câmera
 *
 * @param pixelX - Deslocamento horizontal em pixels
 * @param pixelY - Deslocamento vertical em pixels (profundidade)
 * @param videoWidth - Largura do vídeo em pixels
 * @param videoHeight - Altura do vídeo em pixels
 * @param estimatedDistance - Distância estimada do objeto em metros
 * @returns Deslocamento em metros
 */
export function pixelsToMeters(
  pixelX: number,
  pixelY: number,
  videoWidth: number,
  videoHeight: number,
  estimatedDistance = 10
): VideoDisplacement {
  const horizontalFOV = 60 * DEGREES_TO_RADIANS;
  const realWorldWidth = 2 * estimatedDistance * Math.tan(horizontalFOV / 2);
  const metersPerPixelX = realWorldWidth / videoWidth;

  // Horizontal - mantém o cálculo atual
  const horizontalMeters = pixelX * metersPerPixelX;

  // Profundidade - distribuição linear
  // pixelY = 0 no topo da tela, videoHeight na base da tela
  // Queremos: topo (pixelY=0) = estimatedDistance, base (pixelY=videoHeight) = 0
  const normalizedY = pixelY / videoHeight; // 0 no topo, 1 na base
  const depthMeters = estimatedDistance * (1 - normalizedY);

  return {
    x: horizontalMeters,
    y: depthMeters
  };
}

/**
 * Normaliza um ângulo para o intervalo 0-360
 */
export function normalizeBearing(bearing: number): number {
  return ((bearing % 360) + 360) % 360;
}

/**
 * Calcula a diferença angular entre dois bearings
 * Retorna o menor ângulo entre eles (-180 a +180)
 */
export function bearingDifference(bearing1: number, bearing2: number): number {
  let diff = bearing2 - bearing1;
  while (diff > 180) diff -= 360;
  while (diff < -180) diff += 360;
  return diff;
}
